import type { FilterPanelCheckItem } from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import { arrayToObject, groupBy, last, sortBy, sum, unique, uniqueBy } from 'lib/array/arrayUtils'
import { convertTimeObjectToMinutes, convertMinutesToHoursAndMinutes, convertTimeStringToMinutes, getDisplayInfoV2, getFareClassKey, getFareClassLabel, getTotalStopCount, isCarryOnBaggageIncluded } from 'lib/flights/flightUtils'
import { CHECKOUT_FLIGHT_FILTERS, FlightFilterType, STANDALONE_FLIGHT_FILTERS } from 'constants/flight'
import type { IntlShape } from 'react-intl'
import { getFlightFilterLabel } from 'hooks/Flights/useFlightFilterLabels'
import type { FlightsFiltersValues } from './flightFilterTypes'
import { FlightsFiltersBagType, FlightsFiltersMemberPriceType, FlightsFiltersPriceType, FlightsFiltersRecommendedType } from './flightFilterTypes'
import { getCurrentIntl } from 'i18n/util/currentIntl'

export function getAirlineFilterItems(
  journeys: Array<App.JourneyV2>,
  options: {
    hidePrice?: boolean
  } = {},
): Array<FilterPanelCheckItem> {
  const items = journeys.map(journey => ({
    carrier: journey.carrier,
    name: journey.carrierName,
  }))

  const airlines = sortBy(
    uniqueBy(items, item => item.carrier),
    item => item.name,
    'asc',
  )

  const costs = getAirlinesCheapeastNumbers(journeys)

  return airlines.map(airline => ({
    label: airline.name,
    value: airline.carrier,
    price: !options.hidePrice ? costs[airline.carrier] : undefined,
    hidePrice: options.hidePrice,
  }))
}

function getCombinedJourneyAdultCost(combinedJourney: App.FlightCombinedJourney): number | undefined {
  const departingCost = getJourneyAdultCost(combinedJourney.departingJourney)
  const returningCost = getJourneyAdultCost(combinedJourney.returningJourney)

  if (departingCost === undefined || returningCost === undefined) {
    return undefined
  }

  return departingCost + returningCost
}

function getCombinedJourneyCarrierItems(combinedJourney: App.FlightCombinedJourney) {
  return [
    {
      carrier: combinedJourney.departingJourney.carrier,
      name: combinedJourney.departingJourney.carrierName,
    },
    {
      carrier: combinedJourney.returningJourney.carrier,
      name: combinedJourney.returningJourney.carrierName,
    },
  ]
}

export function getCombinedAirlineFilterItems(
  combinedJourneys: Array<App.FlightCombinedJourney>,
  options: {
    hidePrice?: boolean
  } = {},
): Array<FilterPanelCheckItem> {
  const items = combinedJourneys.flatMap(getCombinedJourneyCarrierItems)

  const airlines = sortBy(
    uniqueBy(items, item => item.carrier),
    item => item.name,
    'asc',
  )

  const costs = getCombinedAirlinesCheapeastNumbers(combinedJourneys)

  return airlines.map(airline => ({
    label: airline.name,
    value: airline.carrier,
    price: !options.hidePrice ? costs[airline.carrier] : undefined,
    hidePrice: options.hidePrice,
  }))
}

function getAirlinesCheapeastNumbers(journeys: Array<App.JourneyV2> = []) {
  return journeys.reduce((costs: Record<string, number>, journey) => {
    const cost = getJourneyAdultCost(journey)

    const carrier = journey.carrier
    const costExists = cost !== undefined

    if (costExists && (cost < costs[carrier] || costs[carrier] === undefined)) {
      costs[carrier] = cost
    }

    return costs
  }, {})
}

function getCombinedAirlinesCheapeastNumbers(combinedJourneys: Array<App.FlightCombinedJourney> = []) {
  return combinedJourneys.reduce((costs: Record<string, number>, combinedJourney) => {
    const cost = getCombinedJourneyAdultCost(combinedJourney)
    const costExists = cost !== undefined

    if (!costExists) {
      return costs
    }

    getCombinedJourneyCarrierItems(combinedJourney).forEach(({ carrier }) => {
      if (cost < costs[carrier] || costs[carrier] === undefined) {
        costs[carrier] = cost
      }
    })

    return costs
  }, {})
}

export function getCabinTypeFilterItems(
  journeys: Array<App.JourneyV2>,
): Array<FilterPanelCheckItem> {
  const intl = getCurrentIntl()
  const cabinTypes = unique(journeys.map(journey => getFareClassKey(journey.flightGroup.flights[0].fareClass)))

  return [
    {
      value: 'all',
      label: intl.formatMessage({
        id: '49EDkBWqTZ9xi07Lh6Oe',
        defaultMessage: 'All cabins',
        description: 'Flight filter option label to include all cabin classes',
      }),
    },
    ...cabinTypes.map(cabinType => ({
      label: getFareClassLabel(cabinType, intl),
      value: cabinType,
    })),
  ]
}

export function getStopoverDurationFilterItems(
  journeys: Array<App.JourneyV2>,
): Array<FilterPanelCheckItem> {
  const allStopoverTimes = journeys.map(
    journey => {
      const timeSum = sum(journey.flightGroup.flights.map(flight => convertTimeObjectToMinutes(flight.stopoverTime)))
      return convertMinutesToHoursAndMinutes(timeSum)
    },
  )

  const uniqueStopOvers = uniqueBy(allStopoverTimes, m => `${m.hours}${m.minutes}`)
  const categoriedTimes = groupBy(uniqueStopOvers, time => time)

  const stopDurations = sortBy(
    categoriedTimes.keys(),
    val => convertTimeObjectToMinutes(val),
    'asc',
  )

  const intl = getCurrentIntl()
  return [
    ...stopDurations.map((duration) => {
      return {
        value: convertTimeObjectToMinutes(duration),
        label: intl.formatMessage(
          {
            id: 'v2AmNKOIy4hN0PcXbHXa',
            defaultMessage: '{hours}h {minutes}m',
            description: 'Stopover duration label in hours and minutes for flight filters',
          },
          { hours: duration.hours, minutes: duration.minutes },
        ),
      }
    }),
  ]
}

export function getStopsFilterItems(
  journeys: Array<App.JourneyV2>,
  options: { hidePrice?: boolean } = {},
): Array<FilterPanelCheckItem> {
  const intl = getCurrentIntl()
  const costs = getStopsBucketCheapestNumbers(journeys)
  const buckets = getAvailableStopBuckets(journeys)

  return buckets.map(bucket => ({
    label: getStopsFilterLabel(bucket, intl),
    value: bucket,
    price: !options.hidePrice ? costs[bucket] : undefined,
  }))
}

export function getCombinedStopsFilterItems(
  combinedJourneys: Array<App.FlightCombinedJourney>,
  options: { hidePrice?: boolean } = {},
): Array<FilterPanelCheckItem> {
  const intl = getCurrentIntl()
  const journeys = combinedJourneys.flatMap(combinedJourney => [
    combinedJourney.departingJourney,
    combinedJourney.returningJourney,
  ])
  const costs = getCombinedStopsBucketCheapestNumbers(combinedJourneys)
  const buckets = getAvailableStopBuckets(journeys)

  return buckets.map(bucket => ({
    label: getStopsFilterLabel(bucket, intl),
    value: bucket,
    price: !options.hidePrice ? costs[bucket] : undefined,
  }))
}

export function getStopoverCitiesFilterItems(
  journeys: Array<App.JourneyV2>,
  options: { hidePrice?: boolean } = {},
): Array<FilterPanelCheckItem> {
  const cities = getJourneyStopOverCities(journeys)
  const stopOverCosts = getStopOverNumbers(journeys)

  return cities.map((city) => ({
    value: city.toLowerCase(),
    label: city,
    price: !options.hidePrice ? stopOverCosts[city] : undefined,
    hidePrice: options.hidePrice,
  }))
}

export function getCombinedStopoverCitiesFilterItems(
  combinedJourneys: Array<App.FlightCombinedJourney>,
  options: { hidePrice?: boolean } = {},
): Array<FilterPanelCheckItem> {
  const cities = getJourneyStopOverCities(combinedJourneys.flatMap(combinedJourney => [
    combinedJourney.departingJourney,
    combinedJourney.returningJourney,
  ]))
  const stopOverCosts = getCombinedStopOverNumbers(combinedJourneys)

  return cities.map((city) => ({
    value: city.toLowerCase(),
    label: city,
    price: !options.hidePrice ? stopOverCosts[city] : undefined,
    hidePrice: options.hidePrice,
  }))
}

const STOP_BUCKET_ORDER = ['0', '1', '2'] as const

function getStopBucketFromCount(stopCount: number): string {
  if (stopCount <= 0) {
    return '0'
  }
  if (stopCount === 1) {
    return '1'
  }
  return '2'
}

function getAvailableStopBuckets(journeys: Array<App.JourneyV2>): Array<string> {
  const buckets = new Set<string>()
  journeys.forEach(journey => {
    buckets.add(getStopBucketFromCount(getTotalStopCount(journey.flightGroup.flights)))
  })
  return STOP_BUCKET_ORDER.filter(bucket => buckets.has(bucket))
}

function getStopsBucketCheapestNumbers(journeys: Array<App.JourneyV2> = []) {
  return journeys.reduce((costs: Record<string, number>, journey) => {
    const cost = getJourneyAdultCost(journey)
    const bucket = getStopBucketFromCount(getTotalStopCount(journey.flightGroup.flights))
    const costExists = cost !== undefined

    if (costExists && (cost < costs[bucket] || costs[bucket] === undefined)) {
      costs[bucket] = cost
    }

    return costs
  }, {})
}

function getCombinedStopsBucketCheapestNumbers(combinedJourneys: Array<App.FlightCombinedJourney> = []) {
  return combinedJourneys.reduce((costs: Record<string, number>, combinedJourney) => {
    const cost = getCombinedJourneyAdultCost(combinedJourney)
    const departingBucket = getStopBucketFromCount(getTotalStopCount(combinedJourney.departingJourney.flightGroup.flights))
    const returningBucket = getStopBucketFromCount(getTotalStopCount(combinedJourney.returningJourney.flightGroup.flights))
    const costExists = cost !== undefined

    if (costExists && departingBucket === returningBucket && (cost < costs[departingBucket] || costs[departingBucket] === undefined)) {
      costs[departingBucket] = cost
    }

    return costs
  }, {})
}

function getStopsFilterLabel(stopBucket: string, intl: IntlShape) {
  if (stopBucket === '0') {
    return intl.formatMessage({
      id: 'BBa0KDJHYJUbjnFTA8E0',
      defaultMessage: 'Direct',
      description: 'Flight filter label for direct flights with no stops in the redesigned filter panel',
    })
  }
  if (stopBucket === '1') {
    return intl.formatMessage({
      id: 't11x2eZBLNdCO5FsCJyM',
      defaultMessage: '1 stop',
      description: 'Flight filter label for flights with one stop in the redesigned filter panel',
    })
  }
  return intl.formatMessage({
    id: 'MCJjMF5aB1pxKtpZSs-V',
    defaultMessage: '2+ stops',
    description: 'Flight filter label for flights with two or more stops in the redesigned filter panel',
  })
}

function getStopOverNumbers(journeys: Array<App.JourneyV2> = []) {
  return journeys.reduce((costs: Record<string, number>, journey) => {
    const cost = getJourneyAdultCost(journey)

    if (journey.flightGroup.flights.length >= 2) {
      const stopOversFlights = journey.flightGroup.flights.slice(0, journey.flightGroup.flights.length - 1)
      const stopCities = stopOversFlights.map(f => f.arrivalCity)
      const costExists = cost !== undefined

      stopCities.forEach(city => {
        if (costExists && (cost < costs[city] || costs[city] === undefined)) {
          costs[city] = cost
        }
      })
    }

    return costs
  }, {})
}

function getCombinedStopOverNumbers(combinedJourneys: Array<App.FlightCombinedJourney> = []) {
  return combinedJourneys.reduce((costs: Record<string, number>, combinedJourney) => {
    const cost = getCombinedJourneyAdultCost(combinedJourney)
    const stopCities = getJourneyStopOverCities([
      combinedJourney.departingJourney,
      combinedJourney.returningJourney,
    ])
    const costExists = cost !== undefined

    stopCities.forEach(city => {
      if (costExists && (cost < costs[city] || costs[city] === undefined)) {
        costs[city] = cost
      }
    })

    return costs
  }, {})
}

function getJourneyStopOverCities(journeys: Array<App.JourneyV2>) {
  const cities: Array<string> = []
  const existingCity: Record<string, boolean> = {}

  journeys.forEach(journey => {
    if (journey.flightGroup.flights.length >= 2) {
      const stopOversFlights = journey.flightGroup.flights.slice(0, journey.flightGroup.flights.length - 1)
      stopOversFlights.forEach(flight => {
        if (!existingCity[flight.arrivalCity]) {
          cities.push(flight.arrivalCity)
          existingCity[flight.arrivalCity] = true
        }
      })
    }
  })

  return cities
}

export function getAllianceFilterItems(
  journeys: Array<App.JourneyV2>,
  options: { hidePrice?: boolean } = {},
): Array<FilterPanelCheckItem> {
  const alliances = getJourneyAlliances(journeys)
  const alliancesCost = getAlliancesNumbers(journeys)

  return alliances.map((alliance) => {
    return {
      value: alliance.toLowerCase(),
      label: alliance,
      price: !options.hidePrice ? alliancesCost[alliance] : undefined,
    }
  })
}

export function getCombinedAllianceFilterItems(
  combinedJourneys: Array<App.FlightCombinedJourney>,
  options: { hidePrice?: boolean } = {},
): Array<FilterPanelCheckItem> {
  const alliances = getJourneyAlliances(combinedJourneys.flatMap(combinedJourney => [
    combinedJourney.departingJourney,
    combinedJourney.returningJourney,
  ]))
  const alliancesCost = getCombinedAlliancesNumbers(combinedJourneys)

  return alliances.map((alliance) => {
    return {
      value: alliance.toLowerCase(),
      label: alliance,
      price: !options.hidePrice ? alliancesCost[alliance] : undefined,
    }
  })
}

function getAlliancesNumbers(journeys: Array<App.JourneyV2> = []) {
  return journeys.reduce((costs: Record<string, number>, journey) => {
    const cost = getJourneyAdultCost(journey)
    const alliance = journey.flightGroup.flights[0]?.alliance

    const costExists = cost !== undefined

    if (costExists && alliance && (cost < costs[alliance] || costs[alliance] === undefined)) {
      costs[alliance] = cost
    }

    return costs
  }, {})
}

function getCombinedAlliancesNumbers(combinedJourneys: Array<App.FlightCombinedJourney> = []) {
  return combinedJourneys.reduce((costs: Record<string, number>, combinedJourney) => {
    const cost = getCombinedJourneyAdultCost(combinedJourney)
    const alliances = getJourneyAlliances([
      combinedJourney.departingJourney,
      combinedJourney.returningJourney,
    ])
    const costExists = cost !== undefined

    alliances.forEach(alliance => {
      if (costExists && (cost < costs[alliance] || costs[alliance] === undefined)) {
        costs[alliance] = cost
      }
    })

    return costs
  }, {})
}

function getJourneyAlliances(journeys: Array<App.JourneyV2>) {
  const alliances: Array<string> = []
  const existingAlliance: Record<string, boolean> = {}

  journeys.forEach(journey => {
    const firstFlight = journey.flightGroup.flights[0]

    if (firstFlight?.alliance && !existingAlliance[firstFlight.alliance]) {
      alliances.push(firstFlight.alliance)
      existingAlliance[firstFlight.alliance] = true
    }
  })

  return alliances
}

export function getDurationFilterItems(
  journeys: Array<App.JourneyV2>,
): Array<FilterPanelCheckItem> {
  const allTimes = journeys.map(journey => journey.flightGroup.totalFlightDuration)
  const categoriedTimes = groupBy(allTimes, time => time)

  const stopDurations = sortBy(
    categoriedTimes.keys(),
    val => convertTimeObjectToMinutes(val),
    'asc',
  )

  const intl = getCurrentIntl()
  return [
    ...stopDurations.map((duration) => {
      return {
        value: convertTimeObjectToMinutes(duration),
        label: intl.formatMessage(
          {
            id: 'v2AmNKOIy4hN0PcXbHXa',
            defaultMessage: '{hours}h {minutes}m',
            description: 'Stopover duration label in hours and minutes for flight filters',
          },
          { hours: duration?.hours, minutes: duration?.minutes },
        ),
      }
    }),
  ]
}

function getJourneyFilterPrice(journey: App.JourneyV2, priceType: FlightsFiltersPriceType): number | undefined {
  return priceType === FlightsFiltersPriceType.ADULT
    ? journey.price.adult.totalFare
    : journey.price.all.totalFare
}

function getCombinedJourneyFilterPrice(
  combinedJourney: App.FlightCombinedJourney,
  priceType: FlightsFiltersPriceType,
): number | undefined {
  const departingCost = getJourneyFilterPrice(combinedJourney.departingJourney, priceType)
  const returningCost = getJourneyFilterPrice(combinedJourney.returningJourney, priceType)

  if (departingCost === undefined || returningCost === undefined) {
    return undefined
  }

  return departingCost + returningCost
}

function buildPriceFilterItems(prices: Array<number>): Array<FilterPanelCheckItem> {
  const uniquePrices = unique(prices).filter((price): price is number => typeof price === 'number' && !Number.isNaN(price))

  return sortBy(uniquePrices, val => val, 'asc').map((price) => ({
    value: price,
    label: `${price}`,
  }))
}

function buildPriceHistogramBuckets(
  prices: Array<number>,
  domainMin: number,
  domainMax: number,
  bucketCount = 21,
): Array<number> {
  if (!prices.length) {
    return []
  }

  if (domainMax <= domainMin) {
    return [prices.length]
  }

  const bucketSize = (domainMax - domainMin) / bucketCount
  const buckets = new Array(bucketCount).fill(0)

  prices.forEach(price => {
    if (price < domainMin || price > domainMax) {
      return
    }

    const index = Math.min(
      bucketCount - 1,
      Math.floor((price - domainMin) / bucketSize),
    )
    buckets[index] += 1
  })

  return buckets
}

export function getPricesFilterItems(
  priceType: FlightsFiltersPriceType,
  journeys: Array<App.JourneyV2>,
): Array<FilterPanelCheckItem> {
  return buildPriceFilterItems(
    journeys
      .map(journey => getJourneyFilterPrice(journey, priceType))
      .filter((price): price is number => typeof price === 'number'),
  )
}

export function getCombinedPricesFilterItems(
  priceType: FlightsFiltersPriceType,
  combinedJourneys: Array<App.FlightCombinedJourney>,
): Array<FilterPanelCheckItem> {
  return buildPriceFilterItems(
    combinedJourneys
      .map(combinedJourney => getCombinedJourneyFilterPrice(combinedJourney, priceType))
      .filter((price): price is number => typeof price === 'number'),
  )
}

export function getPriceHistogramBuckets(
  priceType: FlightsFiltersPriceType,
  journeys: Array<App.JourneyV2>,
  domainMin: number,
  domainMax: number,
  bucketCount = 21,
): Array<number> {
  return buildPriceHistogramBuckets(
    journeys
      .map(journey => getJourneyFilterPrice(journey, priceType))
      .filter((price): price is number => typeof price === 'number'),
    domainMin,
    domainMax,
    bucketCount,
  )
}

export function getCombinedPriceHistogramBuckets(
  priceType: FlightsFiltersPriceType,
  combinedJourneys: Array<App.FlightCombinedJourney>,
  domainMin: number,
  domainMax: number,
  bucketCount = 21,
): Array<number> {
  return buildPriceHistogramBuckets(
    combinedJourneys
      .map(combinedJourney => getCombinedJourneyFilterPrice(combinedJourney, priceType))
      .filter((price): price is number => typeof price === 'number'),
    domainMin,
    domainMax,
    bucketCount,
  )
}

function isJourneyPriceVisible(
  journey: App.JourneyV2,
  filters: FlightsFiltersValues,
): boolean {
  const price = getJourneyFilterPrice(journey, filters.priceFilterType)
  if (price === undefined) {
    return false
  }

  if (filters.minPrices && price < parseInt(filters.minPrices, 10)) {
    return false
  }

  if (filters.maxPrices && price > parseInt(filters.maxPrices, 10)) {
    return false
  }

  return true
}

function isCombinedJourneyPriceVisible(
  combinedJourney: App.FlightCombinedJourney,
  filters: FlightsFiltersValues,
): boolean {
  const price = getCombinedJourneyFilterPrice(combinedJourney, filters.priceFilterType)
  if (price === undefined) {
    return false
  }

  if (filters.minPrices && price < parseInt(filters.minPrices, 10)) {
    return false
  }

  if (filters.maxPrices && price > parseInt(filters.maxPrices, 10)) {
    return false
  }

  return true
}

export function getDepartureTimesFilterItems(
  journeys: Array<App.JourneyV2>,
): Array<FilterPanelCheckItem> {
  const allTimes = journeys.map(journey => journey.flightGroup.flights[0].departingTime)
  const categoriedTimes = groupBy(unique(allTimes), time => time)

  const times = sortBy(
    categoriedTimes.keys(),
    val => val,
    'asc',
  )

  return [
    ...times.map((duration) => {
      return {
        value: convertTimeStringToMinutes(duration),
        label: duration,
      }
    }),
  ]
}

export function getArrivalTimesFilterItems(
  journeys: Array<App.JourneyV2>,
): Array<FilterPanelCheckItem> {
  const allTimes = journeys.map(journey => last(journey.flightGroup.flights).arrivalTime)
  const categoriedTimes = groupBy(unique(allTimes), time => time)

  const times = sortBy(
    categoriedTimes.keys(),
    val => val,
    'asc',
  )

  return [
    ...times.map((duration) => {
      return {
        value: convertTimeStringToMinutes(duration),
        label: duration,
      }
    }),
  ]
}

export function getRecommendedFilterItems(journeys: Array<App.JourneyV2>, options: { hidePrice?: boolean } = {}): Array<FilterPanelCheckItem> {
  const intl = getCurrentIntl()
  const costs = getRecommendedCheapeastNumbers(journeys)
  const recommendedFilterItems: Array<FilterPanelCheckItem> = []

  const hasLuxPlusFare = journeys.some(isJourneyMemberPriceVisible)
  if (hasLuxPlusFare) {
    recommendedFilterItems.push({
      value: FlightsFiltersMemberPriceType.MEMBER_PRICE,
      label: intl.formatMessage({
        id: 'oFRJxm4sVLv6VbHdMOMB',
        defaultMessage: 'LuxPlus+',
        description: 'Recommended flight filter label for LuxPlus member fares in the redesigned filter panel',
      }),
      price: !options.hidePrice ? costs[FlightsFiltersMemberPriceType.MEMBER_PRICE] : undefined,
    })
  }

  const hasCheckedBagFlights = journeys.some(journey => isJourneyCheckedBagVisible(journey))
  if (hasCheckedBagFlights) {
    recommendedFilterItems.push({
      value: FlightsFiltersBagType.CHECKED,
      label: intl.formatMessage({
        id: 'ZEzpQEN5Rtj1I2riF8Dx',
        defaultMessage: 'Includes checked bag',
        description: 'Flight bags filter label for flights with checked baggage included',
      }),
      price: !options.hidePrice ? costs[FlightsFiltersBagType.CHECKED] : undefined,
    })
  }

  const hasCarryOnFlights = journeys.some(journey => isJourneyCarryOnBagVisible(journey))
  if (hasCarryOnFlights) {
    recommendedFilterItems.push({
      value: FlightsFiltersBagType.CARRY_ON,
      label: intl.formatMessage({
        id: '53Lj9x2cHMdEQfWAoOc_',
        defaultMessage: 'Includes carry-on bag',
        description: 'Recommended flight filter label for flights with carry-on baggage included in the redesigned filter panel',
      }),
      price: !options.hidePrice ? costs[FlightsFiltersBagType.CARRY_ON] : undefined,
    })
  }

  return recommendedFilterItems
}

export function getCombinedRecommendedFilterItems(
  combinedJourneys: Array<App.FlightCombinedJourney>,
  options: { hidePrice?: boolean } = {},
): Array<FilterPanelCheckItem> {
  const intl = getCurrentIntl()
  const recommendedFilterItems: Array<FilterPanelCheckItem> = []

  const hasLuxPlusFare = combinedJourneys.some(combinedJourney => (
    isJourneyMemberPriceVisible(combinedJourney.departingJourney) &&
    isJourneyMemberPriceVisible(combinedJourney.returningJourney)
  ))
  if (hasLuxPlusFare) {
    const cheapestLuxPlusFare = combinedJourneys.reduce((cheapest: number | undefined, combinedJourney) => {
      const cost = getCombinedJourneyAdultCost(combinedJourney)
      const isLuxPlus = (
        isJourneyMemberPriceVisible(combinedJourney.departingJourney) &&
        isJourneyMemberPriceVisible(combinedJourney.returningJourney)
      )
      if (!isLuxPlus || cost === undefined) {
        return cheapest
      }
      if (cheapest === undefined || cost < cheapest) {
        return cost
      }
      return cheapest
    }, undefined)

    recommendedFilterItems.push({
      value: FlightsFiltersMemberPriceType.MEMBER_PRICE,
      label: intl.formatMessage({
        id: 'oFRJxm4sVLv6VbHdMOMB',
        defaultMessage: 'LuxPlus+',
        description: 'Recommended flight filter label for LuxPlus member fares in the redesigned filter panel',
      }),
      price: !options.hidePrice ? cheapestLuxPlusFare : undefined,
    })
  }

  if (combinedJourneys.some(combinedJourney => (
    isJourneyCheckedBagVisible(combinedJourney.departingJourney) &&
    isJourneyCheckedBagVisible(combinedJourney.returningJourney)
  ))) {
    recommendedFilterItems.push({
      value: FlightsFiltersBagType.CHECKED,
      label: intl.formatMessage({
        id: 'ZEzpQEN5Rtj1I2riF8Dx',
        defaultMessage: 'Includes checked bag',
        description: 'Flight bags filter label for flights with checked baggage included',
      }),
      price: !options.hidePrice ? getCheapestCombinedJourneyFare(combinedJourneys, isJourneyCheckedBagVisible) : undefined,
    })
  }

  if (combinedJourneys.some(combinedJourney => (
    isJourneyCarryOnBagVisible(combinedJourney.departingJourney) &&
    isJourneyCarryOnBagVisible(combinedJourney.returningJourney)
  ))) {
    recommendedFilterItems.push({
      value: FlightsFiltersBagType.CARRY_ON,
      label: intl.formatMessage({
        id: '53Lj9x2cHMdEQfWAoOc_',
        defaultMessage: 'Includes carry-on bag',
        description: 'Recommended flight filter label for flights with carry-on baggage included in the redesigned filter panel',
      }),
      price: !options.hidePrice ? getCheapestCombinedJourneyFare(combinedJourneys, isJourneyCarryOnBagVisible) : undefined,
    })
  }

  return recommendedFilterItems
}

export function getMemberPriceFilterItems(journeys: Array<App.JourneyV2>, options: { hidePrice?: boolean } = {}): Array<FilterPanelCheckItem> {
  const hasLuxPlusFare = journeys.some(isJourneyMemberPriceVisible)
  if (!hasLuxPlusFare) {
    return []
  }

  const intl = getCurrentIntl()
  const cheapestLuxPlusFare = getCheapestLuxPlusFare(journeys)

  return [
    {
      value: FlightsFiltersMemberPriceType.MEMBER_PRICE,
      label: intl.formatMessage({
        id: 'U7kRWQDSo1GrhtOjzdfU',
        defaultMessage: 'Member prices',
        description: 'Flight filter option label for showing only fares available with LuxPlus member pricing',
      }),
      price: !options.hidePrice ? cheapestLuxPlusFare : undefined,
    },
  ]
}

export function getBagsFilterItems(journeys: Array<App.JourneyV2>, options: { hidePrice?: boolean } = {}): Array<FilterPanelCheckItem> {
  const intl = getCurrentIntl()
  const costs = getRecommendedCheapeastNumbers(journeys)
  const items: Array<FilterPanelCheckItem> = []

  if (journeys.some(journey => isJourneyCheckedBagVisible(journey))) {
    items.push({
      value: FlightsFiltersBagType.CHECKED,
      label: intl.formatMessage({
        id: 'ZEzpQEN5Rtj1I2riF8Dx',
        defaultMessage: 'Includes checked bag',
        description: 'Flight bags filter label for flights with checked baggage included',
      }),
      price: !options.hidePrice ? costs[FlightsFiltersBagType.CHECKED] : undefined,
    })
  }

  if (journeys.some(journey => isJourneyCarryOnBagVisible(journey))) {
    items.push({
      value: FlightsFiltersBagType.CARRY_ON,
      label: intl.formatMessage({
        id: '53Lj9x2cHMdEQfWAoOc_',
        defaultMessage: 'Includes carry-on bag',
        description: 'Recommended flight filter label for flights with carry-on baggage included in the redesigned filter panel',
      }),
      price: !options.hidePrice ? costs[FlightsFiltersBagType.CARRY_ON] : undefined,
    })
  }

  return items
}

export function getCombinedBagsFilterItems(combinedJourneys: Array<App.FlightCombinedJourney>, options: { hidePrice?: boolean } = {}): Array<FilterPanelCheckItem> {
  const intl = getCurrentIntl()
  const cheapestCheckedBagFare = getCheapestCombinedJourneyFare(combinedJourneys, isJourneyCheckedBagVisible)
  const cheapestCarryOnBagFare = getCheapestCombinedJourneyFare(combinedJourneys, isJourneyCarryOnBagVisible)
  const items: Array<FilterPanelCheckItem> = []

  if (combinedJourneys.some(combinedJourney => (
    isJourneyCheckedBagVisible(combinedJourney.departingJourney) &&
    isJourneyCheckedBagVisible(combinedJourney.returningJourney)
  ))) {
    items.push({
      value: FlightsFiltersBagType.CHECKED,
      label: intl.formatMessage({
        id: 'ZEzpQEN5Rtj1I2riF8Dx',
        defaultMessage: 'Includes checked bag',
        description: 'Flight bags filter label for flights with checked baggage included',
      }),
      price: !options.hidePrice ? cheapestCheckedBagFare : undefined,
    })
  }

  if (combinedJourneys.some(combinedJourney => (
    isJourneyCarryOnBagVisible(combinedJourney.departingJourney) &&
    isJourneyCarryOnBagVisible(combinedJourney.returningJourney)
  ))) {
    items.push({
      value: FlightsFiltersBagType.CARRY_ON,
      label: intl.formatMessage({
        id: '53Lj9x2cHMdEQfWAoOc_',
        defaultMessage: 'Includes carry-on bag',
        description: 'Recommended flight filter label for flights with carry-on baggage included in the redesigned filter panel',
      }),
      price: !options.hidePrice ? cheapestCarryOnBagFare : undefined,
    })
  }

  return items
}

function getRecommendedCheapeastNumbers(journeys: Array<App.JourneyV2> = []) {
  return journeys.reduce((costs: Record<string, number>, journey) => {
    const cost = getJourneyAdultCost(journey)
    const existingMemberPriceCost = costs[FlightsFiltersMemberPriceType.MEMBER_PRICE]
    const existingWithCheckedCost = costs[FlightsFiltersBagType.CHECKED]
    const existingWithCarryOnCost = costs[FlightsFiltersBagType.CARRY_ON]

    const costExists = cost !== undefined

    if (costExists) {
      if (isJourneyMemberPriceVisible(journey) && (existingMemberPriceCost === undefined || cost < existingMemberPriceCost)) {
        costs[FlightsFiltersMemberPriceType.MEMBER_PRICE] = cost
      }

      const checkedBaggage = journey.flightGroup.flights[0]?.includedCheckedBaggage
      if (checkedBaggage && (existingWithCheckedCost === undefined || cost < existingWithCheckedCost)) {
        costs[FlightsFiltersBagType.CHECKED] = cost
      }

      if (isJourneyCarryOnBagVisible(journey) && (existingWithCarryOnCost === undefined || cost < existingWithCarryOnCost)) {
        costs[FlightsFiltersBagType.CARRY_ON] = cost
      }
    }

    return costs
  }, {})
}

function getCheapestCombinedJourneyFare(
  combinedJourneys: Array<App.FlightCombinedJourney> = [],
  isJourneyVisible: (journey: App.JourneyV2) => boolean,
) {
  return combinedJourneys.reduce((cheapest: number | undefined, combinedJourney) => {
    const cost = getCombinedJourneyAdultCost(combinedJourney)
    const matches = (
      isJourneyVisible(combinedJourney.departingJourney) &&
      isJourneyVisible(combinedJourney.returningJourney)
    )

    if (cost !== undefined && matches && (cheapest === undefined || cost < cheapest)) {
      return cost
    }

    return cheapest
  }, undefined)
}

function getJourneyAdultCost(journey: App.JourneyV2): number | undefined {
  return journey.price.adult.totalFare
}

function getCheapestLuxPlusFare(journeys: Array<App.JourneyV2> = []) {
  return journeys.reduce((cheapest: number | undefined, journey) => {
    const cost = isJourneyMemberPriceVisible(journey) ? getJourneyAdultCost(journey) : undefined

    if (cost !== undefined && (cheapest === undefined || cost < cheapest)) {
      return cost
    }

    return cheapest
  }, undefined)
}

function isJourneyMarketingAirlineVisible(journey: App.JourneyV2, airlines: Array<string>) {
  return airlines.includes(journey.carrier)
}

function isJourneyStopCountVisible(journey: App.JourneyV2, stops: Array<string>) {
  const stopCount = getTotalStopCount(journey.flightGroup.flights)
  return stops.some(stop => {
    if (stop === '2') {
      return stopCount >= 2
    }
    return stopCount.toString() === stop
  })
}

function isJourneyCarryOnBagVisible(journey: App.JourneyV2) {
  return isCarryOnBaggageIncluded(journey)
}

function getJourneyTotalLayoverMinutes(journey: App.JourneyV2) {
  return sum(journey.flightGroup.flights.map(flight => convertTimeObjectToMinutes(flight.stopoverTime)))
}

function isJourneyCheckedBagVisible(journey: App.JourneyV2) {
  return Boolean(journey.flightGroup.flights[0]?.includedCheckedBaggage)
}

function isJourneyMemberPriceVisible(journey: App.JourneyV2) {
  return Boolean(journey.bookingInfo?.isLuxPlusFare)
}

function isJourneyAllianceVisible(journey: App.JourneyV2, alliances: Array<string>) {
  const flightAlliance = journey.flightGroup.flights[0]?.alliance?.toLowerCase()
  return alliances.some(alliance => alliance === flightAlliance)
}

function isNoStopOverCitiesFilter(stopOverCities: Array<string>) {
  // Select-all off for stopover cities uses the same empty-string sentinel as
  // other multi-select filters, but means "journeys with no stopovers".
  return stopOverCities.length === 1 && stopOverCities[0] === ''
}

function isJourneyStopOverCityVisible(journey: App.JourneyV2, stopOverCities: Array<string>) {
  const flights = journey.flightGroup.flights
  const flightStopOverCities = flights.slice(0, flights.length - 1).map(flight => flight.arrivalCity.toLowerCase())

  if (isNoStopOverCitiesFilter(stopOverCities)) {
    return flightStopOverCities.length === 0
  }

  const selectedCities = arrayToObject(stopOverCities, val => val, val => val)
  return flightStopOverCities.some(city => selectedCities[city])
}

function isJourneyMinDepartureTimeVisible(journey: App.JourneyV2, minDepartureTime: string) {
  return convertTimeStringToMinutes(journey.flightGroup.flights[0].departingTime) >= parseInt(minDepartureTime)
}

function isJourneyMaxDepartureTimeVisible(journey: App.JourneyV2, maxDepartureTime: string) {
  return convertTimeStringToMinutes(journey.flightGroup.flights[0].departingTime) <= parseInt(maxDepartureTime)
}

function isJourneyMinArrivalTimeVisible(journey: App.JourneyV2, minArrivalTime: string) {
  return convertTimeStringToMinutes(last(journey.flightGroup.flights).arrivalTime) >= parseInt(minArrivalTime)
}

function isJourneyMaxArrivalTimeVisible(journey: App.JourneyV2, maxArrivalTime: string) {
  return convertTimeStringToMinutes(last(journey.flightGroup.flights).arrivalTime) <= parseInt(maxArrivalTime)
}

export function isJourneyVisible(
  journey: App.JourneyV2,
  filters: FlightsFiltersValues,
): boolean {
  let show = true

  const group = journey.flightGroup
  const flights = group.flights

  if (filters.stops?.length) {
    const selectedStops = filters.stops.filter(stop => stop !== '')
    if (selectedStops.length) {
      show = show && isJourneyStopCountVisible(journey, selectedStops)
    }
  }

  if (filters.stopDurations) {
    const totalLayover = getJourneyTotalLayoverMinutes(journey)
    show = show && totalLayover <= parseInt(filters.stopDurations)
  }

  if (filters.airlines.length) {
    const selectedAirlines = filters.airlines.filter(airline => airline !== '')
    if (selectedAirlines.length) {
      show = show && isJourneyMarketingAirlineVisible(journey, selectedAirlines)
    }
  }

  if (filters.cabinTypes.length) {
    show = show && flights.some(flight => filters.cabinTypes.includes(getFareClassKey(flight.fareClass)))
  }

  if (filters.minPrices || filters.maxPrices) {
    show = show && isJourneyPriceVisible(journey, filters)
  }

  if (filters.minDepartureTime) {
    show = show && isJourneyMinDepartureTimeVisible(journey, filters.minDepartureTime)
  }

  if (filters.maxDepartureTime) {
    show = show && isJourneyMaxDepartureTimeVisible(journey, filters.maxDepartureTime)
  }

  if (filters.minArrivalTime) {
    show = show && isJourneyMinArrivalTimeVisible(journey, filters.minArrivalTime)
  }

  if (filters.maxArrivalTime) {
    show = show && isJourneyMaxArrivalTimeVisible(journey, filters.maxArrivalTime)
  }

  if (filters.recommended.length) {
    const filterDirectFlights = filters.recommended?.includes(FlightsFiltersRecommendedType.DIRECT_FLIGHTS)
    const filterCheckedBag = filters.recommended?.includes(FlightsFiltersRecommendedType.INCLUDES_CHECKED_BAG)

    if (filterDirectFlights) {
      show = show && flights.length === 1
    }

    if (filterCheckedBag) {
      const info = getDisplayInfoV2(flights)
      show = show && info.baggageIncluded
    }
  }

  if (filters.bags.length) {
    const checkedBag = filters.bags?.includes(FlightsFiltersBagType.CHECKED)
    const carryOnBag = filters.bags?.includes(FlightsFiltersBagType.CARRY_ON)

    if (checkedBag) {
      show = show && isJourneyCheckedBagVisible(journey)
    }

    if (carryOnBag) {
      show = show && isJourneyCarryOnBagVisible(journey)
    }
  }

  if (filters.memberPrices.length) {
    const filterMemberPrice = filters.memberPrices.includes(FlightsFiltersMemberPriceType.MEMBER_PRICE)

    if (filterMemberPrice) {
      show = show && isJourneyMemberPriceVisible(journey)
    }
  }

  if (filters.stopOverCities.length) {
    show = show && isJourneyStopOverCityVisible(journey, filters.stopOverCities)
  }

  if (filters.alliances.length) {
    show = show && isJourneyAllianceVisible(journey, filters.alliances)
  }

  return show
}

export function isCombinedJourneyVisible(
  combinedJourney: App.FlightCombinedJourney,
  filters: FlightsFiltersValues,
): boolean {
  let show = true

  if (filters.stops?.length) {
    const selectedStops = filters.stops.filter(stop => stop !== '')
    if (selectedStops.length) {
      show = show && (
        isJourneyStopCountVisible(combinedJourney.departingJourney, selectedStops) &&
        isJourneyStopCountVisible(combinedJourney.returningJourney, selectedStops)
      )
    }
  }

  if (filters.airlines.length) {
    const selectedAirlines = filters.airlines.filter(airline => airline !== '')
    if (selectedAirlines.length) {
      show = show && (
        isJourneyMarketingAirlineVisible(combinedJourney.departingJourney, selectedAirlines) ||
        isJourneyMarketingAirlineVisible(combinedJourney.returningJourney, selectedAirlines)
      )
    }
  }

  if (filters.stopDurations) {
    const totalLayover = getJourneyTotalLayoverMinutes(combinedJourney.departingJourney) + getJourneyTotalLayoverMinutes(combinedJourney.returningJourney)
    show = show && totalLayover <= parseInt(filters.stopDurations)
  }

  if (filters.minPrices || filters.maxPrices) {
    show = show && isCombinedJourneyPriceVisible(combinedJourney, filters)
  }

  if (filters.bags.length) {
    const checkedBag = filters.bags?.includes(FlightsFiltersBagType.CHECKED)
    const carryOnBag = filters.bags?.includes(FlightsFiltersBagType.CARRY_ON)

    if (checkedBag) {
      show = show && (
        isJourneyCheckedBagVisible(combinedJourney.departingJourney) &&
        isJourneyCheckedBagVisible(combinedJourney.returningJourney)
      )
    }

    if (carryOnBag) {
      show = show && (
        isJourneyCarryOnBagVisible(combinedJourney.departingJourney) &&
        isJourneyCarryOnBagVisible(combinedJourney.returningJourney)
      )
    }
  }

  if (filters.memberPrices.length) {
    const filterMemberPrice = filters.memberPrices.includes(FlightsFiltersMemberPriceType.MEMBER_PRICE)

    if (filterMemberPrice) {
      show = show && (
        isJourneyMemberPriceVisible(combinedJourney.departingJourney) &&
        isJourneyMemberPriceVisible(combinedJourney.returningJourney)
      )
    }
  }

  if (filters.alliances.length) {
    show = show && (
      isJourneyAllianceVisible(combinedJourney.departingJourney, filters.alliances) ||
      isJourneyAllianceVisible(combinedJourney.returningJourney, filters.alliances)
    )
  }

  if (filters.stopOverCities.length) {
    if (isNoStopOverCitiesFilter(filters.stopOverCities)) {
      show = show &&
        isJourneyStopOverCityVisible(combinedJourney.departingJourney, filters.stopOverCities) &&
        isJourneyStopOverCityVisible(combinedJourney.returningJourney, filters.stopOverCities)
    } else {
      show = show && (
        isJourneyStopOverCityVisible(combinedJourney.departingJourney, filters.stopOverCities) ||
        isJourneyStopOverCityVisible(combinedJourney.returningJourney, filters.stopOverCities)
      )
    }
  }

  if (filters.minDepartureTime) {
    show = show && isJourneyMinDepartureTimeVisible(combinedJourney.departingJourney, filters.minDepartureTime)
  }

  if (filters.maxDepartureTime) {
    show = show && isJourneyMaxDepartureTimeVisible(combinedJourney.departingJourney, filters.maxDepartureTime)
  }

  if (filters.minOutboundArrivalTime) {
    show = show && isJourneyMinArrivalTimeVisible(combinedJourney.departingJourney, filters.minOutboundArrivalTime)
  }

  if (filters.maxOutboundArrivalTime) {
    show = show && isJourneyMaxArrivalTimeVisible(combinedJourney.departingJourney, filters.maxOutboundArrivalTime)
  }

  if (filters.minReturnDepartureTime) {
    show = show && isJourneyMinDepartureTimeVisible(combinedJourney.returningJourney, filters.minReturnDepartureTime)
  }

  if (filters.maxReturnDepartureTime) {
    show = show && isJourneyMaxDepartureTimeVisible(combinedJourney.returningJourney, filters.maxReturnDepartureTime)
  }

  if (filters.minArrivalTime) {
    show = show && isJourneyMinArrivalTimeVisible(combinedJourney.returningJourney, filters.minArrivalTime)
  }

  if (filters.maxArrivalTime) {
    show = show && isJourneyMaxArrivalTimeVisible(combinedJourney.returningJourney, filters.maxArrivalTime)
  }

  return show
}

export function removeUnusedFilters(activeFilters: string | Array<string>, segmentFilters: Array<string>) {
  const filtersSet = new Set(segmentFilters)
  return typeof activeFilters === 'object' ? activeFilters.filter(filter => filtersSet.has(filter)) : filtersSet.has(activeFilters)
}

export function getStandaloneMobileFilterPills(intl: IntlShape, filters: Record<string, Array<number | string> | undefined>, activeFilters: Record<string, Set<string> | undefined>) {
  const pills = STANDALONE_FLIGHT_FILTERS.filter(filter => {
    if ([FlightFilterType.TIMES, FlightFilterType.BAGS].includes(filter)) {
      return true
    }

    const activeFilter = filters[filter] || []
    const isSlider = [FlightFilterType.PRICES, FlightFilterType.DURATIONS, FlightFilterType.STOP_DURATIONS].includes(filter)
    return isSlider ? activeFilter.length > 1 : activeFilter.length > 2
  })

  return pills.map(filterKey => {
    const quantity = getFilterQuantity(activeFilters, filterKey)

    return {
      label: getFlightFilterLabel(intl, filterKey),
      value: filterKey,
      quantity,
    }
  }).sort((a, b) => b.quantity - a.quantity)
}

export function getStandaloneAllActiveFilterQuantity(activeFilters: Record<string, Set<string> | undefined>) {
  return STANDALONE_FLIGHT_FILTERS.reduce((acc, filterKey) => {
    const quantity = getFilterQuantity(activeFilters, filterKey)
    if (quantity > 0) return acc + 1
    return acc
  }, 0)
}

export function getFilterQuantity(activeFilters: Record<string, Set<string> | undefined>, filterType: FlightFilterType) {
  let quantity = 0

  if (filterType === FlightFilterType.PRICES) {
    quantity = getPricesFilterQuantity(activeFilters)
  } else if (filterType === FlightFilterType.TIMES) {
    quantity = getTimeFilterQuantity(activeFilters)
  } else {
    const currentFilter = activeFilters[filterType] || new Set([])
    quantity = [...currentFilter].filter(f => {
      if (f === 'all') {
        return false
      }
      // Airlines/Stops select-all-off (or empty opt-in) keeps results unfiltered; don't count as active.
      if ((filterType === FlightFilterType.AIRLINES || filterType === FlightFilterType.STOPS) && f === '') {
        return false
      }
      return true
    }).length
  }

  return quantity
}

function getTimeFilterQuantity(filters: Record<string, Set<string> | undefined>) {
  const minDepartureTime = filters[FlightFilterType.MIN_DEPARTURE_TIME] || new Set([])
  const maxDepartureTime = filters[FlightFilterType.MAX_DEPARTURE_TIME] || new Set([])
  const minOutboundArrivalTime = filters[FlightFilterType.MIN_OUTBOUND_ARRIVAL_TIME] || new Set([])
  const maxOutboundArrivalTime = filters[FlightFilterType.MAX_OUTBOUND_ARRIVAL_TIME] || new Set([])
  const minReturnDepartureTime = filters[FlightFilterType.MIN_RETURN_DEPARTURE_TIME] || new Set([])
  const maxReturnDepartureTime = filters[FlightFilterType.MAX_RETURN_DEPARTURE_TIME] || new Set([])
  const minArrivalTime = filters[FlightFilterType.MIN_ARRIVAL_TIME] || new Set([])
  const maxArrivalTime = filters[FlightFilterType.MAX_ARRIVAL_TIME] || new Set([])

  return Math.max(minDepartureTime.size, maxDepartureTime.size)
    + Math.max(minOutboundArrivalTime.size, maxOutboundArrivalTime.size)
    + Math.max(minReturnDepartureTime.size, maxReturnDepartureTime.size)
    + Math.max(minArrivalTime.size, maxArrivalTime.size)
}

function getPricesFilterQuantity(filters: Record<string, Set<string> | undefined>) {
  const minPrices = filters[FlightFilterType.MIN_PRICES] || new Set([])
  const maxPrices = filters[FlightFilterType.MAX_PRICES] || new Set([])

  return Math.max(minPrices.size, maxPrices.size)
}

export function getCheckoutFilterPills(intl: IntlShape, filters: Record<string, Array<number | string> | undefined>, activeFilters: Record<string, Set<string> | undefined>) {
  const pills = CHECKOUT_FLIGHT_FILTERS.filter(filter => {
    if ([FlightFilterType.TIMES, FlightFilterType.BAGS].includes(filter)) {
      return true
    }

    const activeFilter = filters[filter] || []
    const isSlider = [FlightFilterType.PRICES, FlightFilterType.DURATIONS, FlightFilterType.STOP_DURATIONS].includes(filter)
    return isSlider ? activeFilter.length > 1 : activeFilter.length > 2
  })

  return pills.map(filterKey => {
    const quantity = getFilterQuantity(activeFilters, filterKey)

    return {
      label: getFlightFilterLabel(intl, filterKey),
      value: filterKey,
      quantity,
    }
  }).sort((a, b) => b.quantity - a.quantity)
}

export function getCheckoutAllActiveFilterQuantity(activeFilters: Record<string, Set<string> | undefined>) {
  return CHECKOUT_FLIGHT_FILTERS.reduce((acc, filterKey) => {
    const quantity = getFilterQuantity(activeFilters, filterKey)
    if (quantity > 0) return acc + 1
    return acc
  }, 0)
}
